import React from "react";
import { useTranslation } from "react-i18next";
import berainLogo from "../../../assets/main-logo.png";
import { formatDate } from "../../../utility";
import "../../../style.css";

type PrintHeaderInfo = {
  employeeId?: string;
  employeeName?: string;
  salesOfficeDisplay?: string;
  printedAt?: string;
  printedBy?: string;
};

type ComponentToPrintProps = {
  data: any;
  startDate?: string;
  endDate?: string;
  headerInfo?: PrintHeaderInfo;
};

const formatAreaStreet = (item: { area?: string; street?: string }) => {
  if (item.area && item.street) return `${item.area} / ${item.street}`;
  return item.area ?? item.street ?? "-";
};

const formatCollectionAmount = (value: any) => {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (isNaN(num) || num < 0) return "0.00";
  return num.toFixed(2);
};

const formatQuantity = (value: any) => {
  if (value == null || value === "" || value === "-") return "-";
  const num = Number(value);
  if (isNaN(num)) return value;
  return Math.round(num);
};

const convertDateToDDMMYYYY = (dateString?: string) => {
  if (!dateString) return "-";
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};

const getAbsoluteLogoUrl = (logoPath: string) => {
  if (/^https?:\/\//i.test(logoPath)) {
    return logoPath;
  }
  const normalizedPath = logoPath.startsWith("/") ? logoPath : `/${logoPath}`;
  return `${window.location.origin}${normalizedPath}`;
};

const ComponentToPrint: React.FC<ComponentToPrintProps> = ({
  data,
  startDate,
  endDate,
  headerInfo,
}) => {
  const { t } = useTranslation();

  const skuSummary = data?.sku_summary;
  const skuSummaryPlanned = data?.sku_summary_planned;
  const skuSummaryUnplanned = data?.sku_summary_unplanned;

  return (
    <div id="print-content" className="hidden print:block">
      <div className="flex justify-between items-start mb-6">
        <div className="flex flex-col gap-2">
          {/* <img src={getAbsoluteLogoUrl(berainLogo)} alt="Logo" className="h-12" /> */}
          <p>
            {t("planningReportTableFields.employee_id")}:{" "}
            {headerInfo?.employeeId ?? "-"}
          </p>
          <p>
            {t("planningReportTableFields.employee_name")}:{" "}
            {headerInfo?.employeeName ?? "-"}
          </p>
          <p>
            {t("planningReportTableFields.sales_office")}:{" "}
            {headerInfo?.salesOfficeDisplay ?? "-"}
          </p>
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold">{t("planningReport.printTitle")}</h1>
        </div>
        <div className="text-right">
          {startDate === endDate ? (
            <p>Date: {convertDateToDDMMYYYY(startDate)}</p>
          ) : (
            <p>
              Date: {convertDateToDDMMYYYY(startDate)} to{" "}
              {convertDateToDDMMYYYY(endDate)}
            </p>
          )}
          <p>
            {t("planningReport.printedAt")}:{" "}
            {headerInfo?.printedAt ?? "-"}
          </p>
          <p>
            {t("planningReport.printedBy")}:{" "}
            {headerInfo?.printedBy ?? "-"}
          </p>
        </div>
      </div>

      <table className="w-full mb-8 border-collapse border border-gray-300 table-fixed" style={{ fontSize: "8px" }}>
        <thead>
          <tr>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "4%" }}>
              {t("planningReportTableFields.sequence_number")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "9%" }}>
              {t("planningReportTableFields.customer_id")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "9%" }}>
              {t("planningReportTableFields.delivery_number")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "9%" }}>
              {t("planningReportTableFields.delivery_date")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "9%" }}>
              {t("planningReportTableFields.time_slot")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "7%" }}>
              {t("planningReportTableFields.delivery_type")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "8%" }}>
              {t("planningReportTableFields.collection_amount")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left w-[6%] break-words whitespace-normal" style={{ width: "6%" }}>
              {t("planningReportTableFields.carton_quantity")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "5%" }}>
              {t("planningReportTableFields.free_carton_quantity")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "10%" }}>
              {t("planningReportTableFields.area_street")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "15%" }}>
              {t("planningReportTableFields.customer_name")}
            </th>
            <th className="border border-gray-300 p-1 bg-gray-100 text-left break-words whitespace-normal" style={{ width: "9%" }}>
              {t("planningReportTableFields.customer_mobile_number")}
            </th>
          </tr>
        </thead>
        <tbody>
          {data?.items?.map((item: any, index: number) => (
            <tr key={item.delivery_number ?? `${item.emp_id}-${index}`}>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.sequence_number ?? "-"}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.customer_id ?? "-"}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.delivery_number ?? "-"}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">
                {item.delivery_date ? formatDate(item.delivery_date) : "-"}
              </td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.time_slot ?? "-"}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.delivery_visit_type ?? "-"}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{formatCollectionAmount(item.amount_to_be_paid)}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{formatQuantity(item.carton_quantity)}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{formatQuantity(item.free_carton_quantity)}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{formatAreaStreet(item)}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.customer_name ?? "-"}</td>
              <td className="border border-gray-300 p-1 break-words whitespace-normal">{item.customer_mobile ?? "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {skuSummary && skuSummary.length > 0 && (
        <div className="mt-8 mb-8 page-break-inside-avoid">
          <h2 className="text-base font-bold mb-2">
            {t("planningReport.warehouseLoadingSummaryTitle")}
          </h2>
          <table className="w-full mb-6 border-collapse border border-gray-300" style={{ fontSize: "8px" }}>
            <thead>
              <tr>
                <th className="border border-gray-300 p-1.5 bg-gray-100 text-left">
                  {t("planningReport.material")}
                </th>
                <th className="border border-gray-300 p-1.5 bg-gray-100 text-left">
                  {t("planningReport.description")}
                </th>
                <th className="border border-gray-300 p-1.5 bg-gray-100 text-right">
                  {t("planningReport.totalQty")}
                </th>
              </tr>
            </thead>
            <tbody>
              {skuSummary.map((item: any, idx: number) => (
                <tr key={item.material_id ?? idx}>
                  <td className="border border-gray-300 p-1.5 text-left">{item.material_id ?? "-"}</td>
                  <td className="border border-gray-300 p-1.5 text-left">{item.material_description_en ?? "-"}</td>
                  <td className="border border-gray-300 p-1.5 text-right">{formatQuantity(item.total_carton_quantity ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {((skuSummaryPlanned && skuSummaryPlanned.length > 0) || (skuSummaryUnplanned && skuSummaryUnplanned.length > 0)) && (
        <div className="flex gap-4 w-full mt-8 mb-8 page-break-inside-avoid">
          {skuSummaryPlanned && skuSummaryPlanned.length > 0 && (
            <div className="flex-1">
              <h2 className="text-base font-bold mb-2">
                {t("planningReport.plannedWarehouseLoadingSummaryTitle") || "Planned Warehouse Loading Summary"}
              </h2>
              <table className="w-full mb-6 border-collapse border border-gray-300" style={{ fontSize: "8px" }}>
                <thead>
                  <tr>
                    <th className="border border-gray-300 p-1.5 bg-gray-100 text-left">
                      {t("planningReport.material")}
                    </th>
                    <th className="border border-gray-300 p-1.5 bg-gray-100 text-left">
                      {t("planningReport.description")}
                    </th>
                    <th className="border border-gray-300 p-1.5 bg-gray-100 text-right">
                      {t("planningReport.totalQty")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {skuSummaryPlanned.map((item: any, idx: number) => (
                    <tr key={item.material_id ?? idx}>
                      <td className="border border-gray-300 p-1.5 text-left">{item.material_id ?? "-"}</td>
                      <td className="border border-gray-300 p-1.5 text-left">{item.material_description_en ?? "-"}</td>
                      <td className="border border-gray-300 p-1.5 text-right">{formatQuantity(item.total_carton_quantity ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {skuSummaryUnplanned && skuSummaryUnplanned.length > 0 && (
            <div className="flex-1">
              <h2 className="text-base font-bold mb-2">
                {t("planningReport.unplannedWarehouseLoadingSummaryTitle") || "Unplanned Warehouse Loading Summary"}
              </h2>
              <table className="w-full mb-6 border-collapse border border-gray-300" style={{ fontSize: "8px" }}>
                <thead>
                  <tr>
                    <th className="border border-gray-300 p-1.5 bg-gray-100 text-left">
                      {t("planningReport.material")}
                    </th>
                    <th className="border border-gray-300 p-1.5 bg-gray-100 text-left">
                      {t("planningReport.description")}
                    </th>
                    <th className="border border-gray-300 p-1.5 bg-gray-100 text-right">
                      {t("planningReport.totalQty")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {skuSummaryUnplanned.map((item: any, idx: number) => (
                    <tr key={item.material_id ?? idx}>
                      <td className="border border-gray-300 p-1.5 text-left">{item.material_id ?? "-"}</td>
                      <td className="border border-gray-300 p-1.5 text-left">{item.material_description_en ?? "-"}</td>
                      <td className="border border-gray-300 p-1.5 text-right">{formatQuantity(item.total_carton_quantity ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComponentToPrint;
